import {Injectable} from '@angular/core';
import {merge, NEVER, Observable, of, Subject, switchMap} from 'rxjs';
import {debounceTime, map, share, takeUntil, tap} from 'rxjs/operators';
import {AuthService} from '../../components/auth/auth.service';
import {destroy$} from '../../components/destroyable';
import {Side} from '../../enums/side';
import {SettingsApiService} from '../../services/settings-api.service';

interface DexLastTradeParamsForKey {
    trancheSymbol: string;
    side: Side;
    assetSymbol: string;
    qty: string;
    ts?: number;
}

const PARAMS_EXPIRATION_TIMEOUT_MS = 1000 * 60 * 60 * 24 * 7; // 1 week

type DexLastTradeParams = Record<string, DexLastTradeParamsForKey>;

const SETTINGS_API_LAST_TRADE_PARAMS_KEY = 'web-dex-last-trade-params';

const STORAGE_DEBOUNCE_TIME_MS = 1000 * 5; // 5 seconds

@Injectable()
export class DexLastTradeParamsService {

    private isResolved: boolean = false;

    private paramsByKey: DexLastTradeParams | undefined;

    private resolving$: Observable<DexLastTradeParams> | undefined;

    private readonly destroy$ = destroy$();

    private readonly storeTrigger$ = new Subject<DexLastTradeParams>();

    constructor(
        private readonly settingsApiService: SettingsApiService,
        private readonly authService: AuthService
    ) {
        this.initListeners();
    }

    public resolve(): Observable<DexLastTradeParams | undefined> {
        if (this.isResolved) {
            return of(this.paramsByKey);
        }

        if (!this.authService.userRole) {
            return of({});
        }

        this.resolving$ ??= this.fetch().pipe(
            tap(params => {
                this.paramsByKey = params;
                this.isResolved = true;
                this.resolving$ = undefined;
            }),
            takeUntil(this.destroy$),
            share()
        );

        return this.resolving$;
    }

    public preResolve(): Observable<undefined> {
        this.resolve().subscribe();

        return of(undefined);
    }

    public storeForKey(paramsForKey: DexLastTradeParamsForKey): void {
        const key = this.getKey(paramsForKey.trancheSymbol, paramsForKey.side);
        const known = this.paramsByKey?.[key];

        const isSame = !!known
            && known.assetSymbol === paramsForKey.assetSymbol
            && known.qty === paramsForKey.qty;

        if (isSame) {
            return;
        }

        const params: DexLastTradeParams = {
            ...this.paramsByKey,
            [key]: {
                ...paramsForKey,
                ts: undefined
            }
        };

        this.paramsByKey = params;
        this.storeTrigger$.next(params);
    }

    public getForKey(trancheSymbol: string, side: Side): DexLastTradeParamsForKey {
        if (!this.isResolved) {
            console.error('Retrieving last trade params while they are not resolved.');
        }

        const key = this.getKey(trancheSymbol, side);

        return this.paramsByKey?.[key];
    }

    private getKey(trancheSymbol: string, side: Side): string {
        return [
            trancheSymbol,
            side
        ].join(':');
    }

    private initListeners(): void {
        this.authService.userRole$.pipe(
            switchMap(userRole => {
                if (!userRole) {
                    this.isResolved = false;
                    this.paramsByKey = undefined;
                    this.resolving$ = undefined;

                    return NEVER;
                } else {
                    return merge(
                        this.resolve(),
                        this.storeTrigger$.pipe(
                            debounceTime(STORAGE_DEBOUNCE_TIME_MS),
                            switchMap(params => this.storeInApi(params))
                        )
                    );
                }
            }),
            takeUntil(this.destroy$)
        ).subscribe();
    }


    private fetch(): Observable<DexLastTradeParams> {
        return this.settingsApiService.fetch<DexLastTradeParams | null>(SETTINGS_API_LAST_TRADE_PARAMS_KEY).pipe(
            map(params =>
                params ? this.cleanUpParams(params) : {}
            )
        );
    }

    private storeInApi(params: DexLastTradeParams): Observable<any> {
        const ts = Date.now();
        Object.values(params).forEach(p =>
            p.ts ??= ts
        );

        return this.settingsApiService.store<DexLastTradeParams>(SETTINGS_API_LAST_TRADE_PARAMS_KEY, params);
    }

    private cleanUpParams(params: DexLastTradeParams): DexLastTradeParams {
        const oldestTs = Date.now() - PARAMS_EXPIRATION_TIMEOUT_MS;

        Object.keys(params).forEach(key => {
            const p = params[key];

            if (!p.ts || p.ts <= oldestTs) {
                delete params[key];
            }
        });

        return params;
    }

}
